// Copyright © 2019-2026 Focal Systems. All rights reserved.

import { useQuery } from "@tanstack/react-query";
import { api } from "@/api/client";
import type { components } from "@/api/schema";

export type DevicesSortBy = components["schemas"]["DevicesSortByEnum"];
type SortOrder = components["schemas"]["HttpSortingOrderEnum"];

export type DeviceEntry = components["schemas"]["DevicesListEntryResponse"];
export type DeviceChainRef = components["schemas"]["ChainsRelevantResponse"];
export type DeviceStoreRef = components["schemas"]["StoreResponse"];

export interface DevicesListResult {
  devices: DeviceEntry[];
  chains: DeviceChainRef[];
  stores: DeviceStoreRef[];
  count: number;
}

interface DevicesListParams {
  page?: number;
  limit?: number;
  search?: string;
  sortBy?: DevicesSortBy;
  orderBy?: SortOrder;
  chainName?: string;
  storeName?: string;
  onlyActive?: boolean;
  enabled?: boolean;
}

export function useDevicesQuery(params: DevicesListParams = {}) {
  const { page = 1, limit = 20, search, sortBy, orderBy, chainName, storeName, onlyActive, enabled } = params;

  return useQuery({
    queryKey: ["devices", "list", { page, limit, search, sortBy, orderBy, chainName, storeName, onlyActive }],
    enabled,
    queryFn: async () => {
      const res = await api.GET("/v1/hardware/devices/", {
        params: {
          query: {
            page,
            limit,
            search: search || undefined,
            sortBy,
            orderBy,
            "filter[chainName]": chainName || undefined,
            "filter[storeName]": storeName || undefined,
            "filter[onlyActive]": onlyActive,
          },
        },
      });
      return res.data as unknown as DevicesListResult;
    },
  });
}
